// Shared types for tasks.waddingtonpark.com.au
// Used by both frontend and backend

export interface Task {
  id: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  syncedAt?: string;
  
  // Task content
  title: string;
  description?: string;
  notes?: string;
  location?: Location;
  
  // Media attachments
  photos?: Photo[];
  audioRecordings?: AudioRecording[];
  
  // Status and metadata
  status: TaskStatus;
  priority: TaskPriority;
  category: TaskCategory;
  estimatedDuration?: number; // minutes
  
  // Sync metadata
  localId?: string; // UUID for offline tasks
  syncStatus: SyncStatus;
  retryCount?: number;
  lastError?: string;
}

export interface Photo {
  id: string;
  taskId: string;
  filename: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  capturedAt: string;
  
  // Local storage
  localUrl?: string;
  localBlob?: Blob;
  
  // Cloud storage
  s3Key?: string;
  s3Url?: string;
  uploadedAt?: string;
  
  // Sync
  syncStatus: SyncStatus;
}

export interface AudioRecording {
  id: string;
  taskId: string;
  filename: string;
  mimeType: string;
  size: number;
  duration: number; // seconds
  recordedAt: string;
  
  // Local storage
  localUrl?: string;
  localBlob?: Blob;
  
  // Cloud storage
  s3Key?: string;
  s3Url?: string;
  uploadedAt?: string;
  
  // Sync
  syncStatus: SyncStatus;
}

export interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
  capturedAt: string;
  address?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  createdAt: string;
  lastLoginAt?: string;
  isActive: boolean;
}

// Enums
export enum TaskStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum TaskCategory {
  MAINTENANCE = 'maintenance',
  INSPECTION = 'inspection',
  HARVEST = 'harvest',
  PLANTING = 'planting',
  IRRIGATION = 'irrigation',
  PEST_CONTROL = 'pest_control',
  EQUIPMENT = 'equipment',
  SAFETY = 'safety',
  OTHER = 'other'
}

export enum SyncStatus {
  PENDING = 'pending',
  SYNCING = 'syncing',
  SYNCED = 'synced',
  ERROR = 'error',
  CONFLICT = 'conflict'
}

export enum UserRole {
  WORKER = 'worker',
  SUPERVISOR = 'supervisor',
  MANAGER = 'manager',
  ADMIN = 'admin'
}

// API Request/Response types
export interface CreateTaskRequest {
  title: string;
  description?: string;
  notes?: string;
  location?: Omit<Location, 'capturedAt'>;
  priority: TaskPriority;
  category: TaskCategory;
  estimatedDuration?: number;
  photos?: File[];
  audioRecordings?: File[];
}

export interface CreateTaskResponse {
  success: boolean;
  task?: Task;
  error?: string;
  validationErrors?: ValidationError[];
}

export interface GetTasksResponse {
  success: boolean;
  tasks?: Task[];
  pagination?: PaginationInfo;
  error?: string;
}

export interface UpdateTaskRequest {
  id: string;
  status?: TaskStatus;
  notes?: string;
  photos?: File[];
  audioRecordings?: File[];
}

export interface UpdateTaskResponse {
  success: boolean;
  task?: Task;
  error?: string;
}

export interface SyncRequest {
  tasks: Task[];
  photos: Photo[];
  audioRecordings: AudioRecording[];
  lastSyncAt?: string;
}

export interface SyncResponse {
  success: boolean;
  syncedTasks?: Task[];
  syncedPhotos?: Photo[];
  syncedAudioRecordings?: AudioRecording[];
  conflicts?: SyncConflict[];
  error?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface SyncConflict {
  resourceType: 'task' | 'photo' | 'audio';
  resourceId: string;
  localVersion: any;
  remoteVersion: any;
  conflictReason: string;
}

// API Error types
export interface ApiError {
  code: string;
  message: string;
  details?: any;
  timestamp: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  pagination?: PaginationInfo;
}

// Authentication types
export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  permissions: string[];
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

// Local storage types for offline functionality
export interface OfflineTask extends Omit<Task, 'id'> {
  localId: string;
  createdOffline: boolean;
  queuedForSync: boolean;
}

export interface SyncQueue {
  id: string;
  type: 'create' | 'update' | 'delete';
  resourceType: 'task' | 'photo' | 'audio';
  resourceId: string;
  data: any;
  createdAt: string;
  retryCount: number;
  lastAttempt?: string;
  error?: string;
}

// Configuration types
export interface AppConfig {
  apiBaseUrl: string;
  awsRegion: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  s3BucketName: string;
  maxPhotoSize: number; // bytes
  maxAudioDuration: number; // seconds
  syncInterval: number; // milliseconds
  retryAttempts: number;
  offlineStorageQuota: number; // bytes
}

// Form validation schemas (compatible with Joi and Yup)
export interface TaskValidationSchema {
  title: {
    required: true;
    minLength: 3;
    maxLength: 200;
  };
  description: {
    maxLength: 1000;
  };
  notes: {
    maxLength: 2000;
  };
  priority: {
    required: true;
    enum: TaskPriority[];
  };
  category: {
    required: true;
    enum: TaskCategory[];
  };
  estimatedDuration: {
    min: 1;
    max: 480; // 8 hours in minutes
  };
} 